export type INotification =
  | IAnnouncementNotification
  | IBadgeNotification
  | ICommentNotification
  | IGoalNotification
  | IMentionNotification
  | IMetricNotification
  | IMetricUpdateNotification
  | IReactionNotification
  | ITaskNotification
  | ISecondaryUserInvitationNotification
  | IKpiNotification;

export enum TargetType {
  Announcements = "announcements",
  BadgeType = "badge",
  CommentType = "comment",
  GoalType = "goal",
  MetricType = "metric",
  MetricSnapshotType = "metric_snapshot",
  MentionsFieldType = "mentions",
  ReactionType = "reaction",
  SecondaryUserType = "secondary_user",
  TaskType = "task",
  KpiType = "kpi",
}

export interface INotificationBase<D extends INotificationDataItem, T extends TargetType> {
  accountId: string;
  createdAt: string;
  createdById: string;
  id: string;
  notificationData: INotificationData<D>;
  operationType: string;
  seen: boolean;
  targetId: string;
  targetType: T;
  userId: string;
}

export type IAnnouncementNotification = INotificationBase<IAnnouncementNotificationDataItem, TargetType.Announcements>;
export type IBadgeNotification = INotificationBase<IBadgeNotificationDataItem, TargetType.BadgeType>;
export type ICommentNotification = INotificationBase<ICommentNotificationDataItem, TargetType.CommentType>;
export type IGoalNotification = INotificationBase<IGoalNotificationDataItem, TargetType.GoalType>;
export type IMentionNotification = INotificationBase<IMentionNotificationDataItem, TargetType.MentionsFieldType>;
export type IMetricNotification = INotificationBase<IMetricNotificationDataItem, TargetType.MetricType>;
export type IMetricUpdateNotification = INotificationBase<IMetricUpdateNotificationDataItem, TargetType.MetricSnapshotType>;
export type IReactionNotification = INotificationBase<IReactionNotificationDataItem, TargetType.ReactionType>;
export type ITaskNotification = INotificationBase<ITaskNotificationDataItem, TargetType.TaskType>;
export type ISecondaryUserInvitationNotification = INotificationBase<ISecondaryUserInvitationNotificationDataItem, TargetType.SecondaryUserType>;
export type IKpiNotification = INotificationBase<IKpiNotificationDataItem, TargetType.KpiType>;

export interface INotificationData<D extends INotificationDataItem> {
  target: INotificationUser;
  actor: INotificationUser;
  assignees: INotificationUser[];
  userNotificationSettingsUrl: string;
  url: string;
  item: D;
  channel: string;
}

export interface INotificationUser {
  id: string;
  names: string;
  avatar: string;
  email: string;
  url: string;
  language: string;
}

export type INotificationDataItem =
  | IAnnouncementNotificationDataItem
  | IBadgeNotificationDataItem
  | ICommentNotificationDataItem
  | IGoalNotificationDataItem
  | IMentionNotificationDataItem
  | IMetricNotificationDataItem
  | IMetricUpdateNotificationDataItem
  | IReactionNotificationDataItem
  | ITaskNotificationDataItem
  | ISecondaryUserInvitationNotificationDataItem
  | IKpiNotificationDataItem;

enum AnnouncementType {
  AnnouncementRecognition = 0,
  AnnouncementTextPost = 1,
}

export interface IAnnouncementNotificationDataItem {
  comment: string;
  url: string;
  name: string;
  announcementType: AnnouncementType;
}

interface IBadgeNotificationDataItem {
  badgeName: string;
  comment: string;
  url: string;
}

interface ICommentNotificationDataItem {
  text: string;
  targetType: string;
  targetTitle: string;
  targetItem: IMetricUpdateNotificationDataItem;
  isMention: boolean;
  url: string;
}

interface IGoalNotificationDataItem {
  name: string;
  url: string;
  description: string;
  sessionName: string;
  changes: IGoalUpdateChanges;
  parent: string;
  parentType: string;
  assignee: INotificationUser;
  tags: string;
  isTeamObjective: boolean;
}

export interface IGoalUpdateChanges {
  name: IDiff;
  assignee: IDiff;
  parent: IDiff;
  parentType: IDiff;
  tags: IDiff;
  description: IDiff;
}

interface IDiff {
  oldValue: string | INotificationUser | number;
  newValue: string | INotificationUser | number;
}

interface IMentionNotificationDataItem {
  title: string;
  targetType: string;
  targetUrl: string;
  fieldName: string;
  fieldValue: string;
}

export interface IMetricNotificationDataItem {
  name: string;
  actual: number;
  attainment: number;
  goalName: string;
  url: string;
  targetOperator: string;
  description: string;
  changes: IMetricChanges;
  softDueDate?: string;
  dueDate?: string;
  assignee: INotificationUser;
  assignees?: INotificationUser[];
  templatedNotificationTextTranslated?: string;
  targetInfo: IMetricTargetInfo;
  isTeamMetric: boolean;
}

interface IMetricUpdateNotificationDataItem {
  name: string;
  goalName: string;
  updateNote: string;
  updatedValue: IMetricTargetInfo;
  target: number;
  url: string;
  targetOperator: string;
  description: string;
  changes: IMetricUpdateChanges;
  assignee: INotificationUser;
  targetInfo: IMetricTargetInfo;
  isTeamMetric: boolean;
  modifiedBy: string;
}

export interface IMetricChanges {
  name: IDiff;
  goalName: IDiff;
  assignee: IDiff;
  attainment: IDiff;
  initialValue: IDiff;
  targetOperator: IDiff;
  softDueDate: IDiff;
  dueDate: IDiff;
  targetInfo: IDiff;
  description: IDiff;
}

export interface IMetricTargetInfo {
  critical: number;
  target: number;
  manualType: string;
  format: IMetricFormat;
}

interface IMetricFormat {
  prefix: string;
  suffix: string;
}

interface IMetricUpdateChanges {
  updateNote: IDiff;
}

interface IReactionNotificationDataItem {
  targetId: string;
  targetType: string;
  url: string;
  targetItem: IMetricUpdateNotificationDataItem;
  content: string;
  reactedUsers: INotificationUser[];
  reactions: IReaction[];
  targetItemType?: string;
}

interface IReaction {
  name: string;
  emoji: string;
  users: INotificationUser[];
}

interface ISecondaryUserInvitationNotificationDataItem {
  accountName: string;
}

interface ITaskNotificationDataItem {
  id: string;
  ownerID: string;
  name: string;
  description: string;
  url: string;
  isTeam: boolean;
  assignee: INotificationUser;
  dueDate: string;
}

export interface IKpiNotificationDataItem {
  id: string;
  ownerId: string;
  ownerIds: string;
  name: string;
  description: string;
  url: string;
  isTeam: boolean;
  assignee: INotificationUser;
}

interface INotificationSettingsEmbedded {
  announcements: boolean;
  badges: boolean;
  krProgressUpdateMe: boolean;
  krProgressUpdateTeam: boolean;
  mentionsInComment: boolean;
  objectiveApprovals: boolean;
  okrsAndTasksAssignedMeMyTeam: boolean;
  okrsEditedByMe: boolean;
  okrsEditedByTeam: boolean;
  kpiAssignedMeMyTeam: boolean;
  taskAssignedMeMyTeam: boolean;
}

export interface INotificationSettings {
  emailNotificationSettings: {
    embedded: INotificationSettingsEmbedded;
  };
  inboxNotificationSettings: {
    embedded: INotificationSettingsEmbedded;
  };
  slackNotificationSettings: {
    embedded: INotificationSettingsEmbedded;
  };
}
export interface INotificationOptions {
  subject: string;
  email: boolean;
  slack: boolean;
  inbox: boolean;
}
export interface IPatchedNotificationSettings {
  emailNotificationSettings?: {
    embedded: Partial<INotificationSettingsEmbedded>;
  };
  inboxNotificationSettings?: {
    embedded: Partial<INotificationSettingsEmbedded>;
  };
  slackNotificationSettings?: {
    embedded: {
      announcements?: boolean;
      badges?: boolean;
      conversations?: boolean;
      krProgressUpdateMe?: boolean;
      krProgressUpdateTeam?: boolean;
      mentionsInComment?: boolean;
      objectiveApprovals?: boolean;
      okrsAndTasksAssignedMeMyTeam?: boolean;
      okrsEditedByMe?: boolean;
      okrsEditedByTeam?: boolean;
      kpiAssignedMeMyTeam?: boolean;
    };
  };
}

export interface INotificationIndicators {
  loading?: boolean;
  markingAllAsSeen?: boolean;
  markingAsSeen?: boolean;
  loadingMoreMessages?: boolean;
}
